import { SERVICE_STATUS_COLORS } from '@markku/types'
import dayjs from 'dayjs'
import { subscriptionHooks } from '$src/lib/trpcHooks'

export type ServiceStatusData = NonNullable<
  ReturnType<typeof subscriptionHooks.useServiceStatusByProperty>['data']
>
export type Service = ServiceStatusData['services'][number]

interface StatusMeta {
  color: string
}

// RAG → liikennevalo colour, reusing the shared SERVICE_STATUS_COLORS map.
const STATUS_META: Record<string, StatusMeta> = {
  overdue: { color: SERVICE_STATUS_COLORS.overdue },
  due_soon: { color: SERVICE_STATUS_COLORS.due_soon },
  scheduled: { color: SERVICE_STATUS_COLORS.scheduled },
  completed: { color: SERVICE_STATUS_COLORS.completed },
  pending: { color: SERVICE_STATUS_COLORS.pending },
}

export const getStatusMeta = (status: string): StatusMeta =>
  STATUS_META[status] ?? STATUS_META.pending

// Most actionable first: overdue → due soon → scheduled → completed → pending.
const STATUS_RANK: Record<string, number> = {
  overdue: 0,
  due_soon: 1,
  scheduled: 2,
  completed: 3,
  pending: 4,
}

export const sortByPriority = (services: Service[]): Service[] =>
  [...services].sort(
    (a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9),
  )

/** Whole-day difference from today; negative = in the past. */
export const daysFromToday = (date: string | null): number | null =>
  date ? dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day') : null

export interface GroupedServices {
  counts: { overdue: number; dueSoon: number; onTrack: number }
}

export function groupServices(services: Service[]): GroupedServices {
  const overdue = services.filter((s) => s.status === 'overdue').length
  const dueSoon = services.filter((s) => s.status === 'due_soon').length
  return {
    counts: { overdue, dueSoon, onTrack: services.length - overdue - dueSoon },
  }
}
